import React, { useEffect, useState } from 'react';
import { Megaphone, Sparkles, TrendingUp, X } from 'lucide-react';
import api from '../../services/api';
import { formatCurrency } from '../../utils/currency';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import FeatureLockedPage from '../../components/FeatureLockedPage';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';
import alertService from '../../utils/alert';

// AdCampaign.budget/totalSpend are stored as literal USD cents (no per-record
// currency field on the model) — format as real USD rather than mislabeling
// stored USD amounts under the tenant's display currency.
const AD_SPEND_CURRENCY = 'USD';

const PLATFORM_META = {
  google: { label: 'Google Ads', emoji: '🔍', color: '#4285F4' },
  meta: { label: 'Meta Ads', emoji: '📘', color: '#1877F2' },
  linkedin: { label: 'LinkedIn Ads', emoji: '💼', color: '#0A66C2' },
};

const STATUS_BADGE = {
  draft: 'bg-[#F3F2F2] text-[#706E6B]',
  active: 'bg-[#E3F3E8] text-[#036B3A]',
  paused: 'bg-[#FFF4E5] text-[#8C4B02]',
  completed: 'bg-[#D6EAFB] text-[#0176D3]',
  error: 'bg-[#FDE7E9] text-[#BA0517]',
};

// ── AI Copy Generator ─────────────────────────────────────────────────────────

function CopyGenerator({ platform, onApply }) {
  const [form, setForm] = useState({ product: '', audience: '', tone: 'professional', cta: 'Learn More', count: 3 });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/ads/generate/copy', { platform, ...form });
      setResults(data.data || []);
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {[['product', 'Product / Service'], ['audience', 'Target Audience'], ['tone', 'Tone'], ['cta', 'Call to Action']].map(([k, l]) => (
          <div key={k}>
            <label className="block text-xs font-medium text-[#706E6B] mb-1">{l}</label>
            <input value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
              className="w-full border border-[#D8DDE6] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
              placeholder={l} />
          </div>
        ))}
      </div>
      <button onClick={generate} disabled={loading || !form.product || !form.audience}
        className="bg-[#0176D3] hover:bg-[#014486] disabled:opacity-50 text-white h-9 px-4 rounded text-[13px] font-semibold whitespace-nowrap">
        {loading ? '✨ Generating…' : '✨ Generate Copy'}
      </button>
      {results.map((v, i) => (
        <div key={i} className="border border-[#E5E5E5] rounded-lg p-4 bg-[#F7F9FB]">
          <div className="flex items-start justify-between mb-2">
            <p className="text-sm font-semibold text-[#444444]">{v.name || `Variant ${i + 1}`}</p>
            {onApply && (
              <button onClick={() => onApply(v)}
                className="h-8 px-3 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] whitespace-nowrap">Use this</button>
            )}
          </div>
          <p className="text-sm font-bold text-[#080707]">{v.headline}</p>
          <p className="text-xs text-[#5C5C5C] mt-1 leading-relaxed">{v.body}</p>
          <p className="text-xs text-[#0176D3] mt-1 font-medium">{v.cta}</p>
        </div>
      ))}
    </div>
  );
}

// ── Campaign Form ─────────────────────────────────────────────────────────────

function CampaignForm({ onCreated, onClose }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: '', platform: 'meta', objective: 'leads', budget: 1000, abTestEnabled: false });
  const [variants, setVariants] = useState([{ name: 'Variant A', headline: '', body: '', cta: 'Learn More' }]);
  const [saving, setSaving] = useState(false);

  const applyGeneratedCopy = (variant) => {
    setVariants(vs => [...vs, { ...variant, name: `Variant ${String.fromCharCode(65 + vs.length)}` }]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/ads/campaigns', { ...form, variants });
      onCreated();
      onClose();
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-2xl max-h-[100dvh] overflow-y-auto p-4 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-[16px] font-bold text-[#2E2E2E]">New Ad Campaign</h2>
          <button onClick={onClose} className="text-[#5C5C5C] hover:text-[#2E2E2E]" aria-label="Close"><X size={18} /></button>
        </div>

        {/* Step tabs */}
        <div className="flex gap-1 mb-6">
          {[['1', 'Basics'], ['2', 'AI Copy']].map(([s, l]) => (
            <button key={s} onClick={() => setStep(Number(s))}
              className={`px-4 py-1.5 rounded text-[13px] font-semibold ${step === Number(s) ? 'bg-[#0176D3] text-white' : 'bg-[#F3F2F2] text-[#5C5C5C]'}`}>
              {s}. {l}
            </button>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-[#706E6B] mb-1">Campaign Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="w-full border border-[#D8DDE6] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-[#706E6B] mb-1">Platform</label>
                <select value={form.platform} onChange={e => setForm(f => ({ ...f, platform: e.target.value }))}
                  className="w-full border border-[#D8DDE6] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]">
                  {Object.entries(PLATFORM_META).map(([k, v]) => <option key={k} value={k}>{v.emoji} {v.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#706E6B] mb-1">Objective</label>
                <select value={form.objective} onChange={e => setForm(f => ({ ...f, objective: e.target.value }))}
                  className="w-full border border-[#D8DDE6] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]">
                  {['leads', 'traffic', 'awareness', 'conversions'].map(o => <option key={o} value={o} className="capitalize">{o}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[#706E6B] mb-1">Daily Budget ({AD_SPEND_CURRENCY})</label>
              <input type="number" value={form.budget / 100} onChange={e => setForm(f => ({ ...f, budget: Math.round(e.target.value * 100) }))}
                className="w-full border border-[#D8DDE6] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
                placeholder="e.g. 50" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="abtest" checked={form.abTestEnabled} onChange={e => setForm(f => ({ ...f, abTestEnabled: e.target.checked }))} className="h-4 w-4 text-[#0176D3]" />
              <label htmlFor="abtest" className="text-sm text-[#444444]">Enable A/B testing across variants</label>
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => setStep(2)} className="h-9 px-4 bg-[#0176D3] hover:bg-[#014486] text-white rounded text-[13px] font-semibold whitespace-nowrap">Next: AI Copy →</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <p className="text-sm text-[#706E6B] mb-4">Generate AI copy variants or add them manually. Generated variants will be included in the campaign.</p>
            <CopyGenerator platform={form.platform} onApply={applyGeneratedCopy} />
            <div className="mt-5 border-t border-[#F3F2F2] pt-4">
              <p className="text-xs font-medium text-[#706E6B] mb-2">Variants ({variants.length})</p>
              {variants.map((v, i) => (
                <div key={i} className="flex items-center gap-2 p-2 border border-[#F3F2F2] rounded-lg mb-1">
                  <span className="text-xs font-medium text-[#706E6B] w-20">{v.name}</span>
                  <span className="text-xs text-[#444444] truncate flex-1">{v.headline || '(no headline)'}</span>
                  <button onClick={() => setVariants(vs => vs.filter((_, j) => j !== i))}
                    className="text-xs text-[#BA0517] hover:text-[#BA0517]">✕</button>
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-4">
              <button onClick={() => setStep(1)} className="h-9 px-4 bg-white border border-[#C9C9C9] text-[#0176D3] rounded text-[13px] font-semibold whitespace-nowrap">← Back</button>
              <button onClick={save} disabled={saving || !form.name}
                className="bg-[#0176D3] hover:bg-[#014486] disabled:opacity-50 text-white h-9 px-4 rounded text-[13px] font-semibold whitespace-nowrap">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdsPage() {
  const { hasAccess, plan, loading: featureLoading } = useFeatureAccess('ads_management');
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [platformFilter, setPlatformFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = platformFilter ? `?platform=${platformFilter}` : '';
      const { data } = await api.get(`/ads/campaigns${params}`);
      setCampaigns(data.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (hasAccess) load(); }, [platformFilter, hasAccess]);

  if (featureLoading) return null;

  if (!hasAccess) {
    return (
      <FeatureLockedPage
        title="Meta Ads"
        subtitle="Facebook & Instagram campaigns with AI-generated creative"
        icon={Megaphone}
        plan={plan}
        featureName="Meta Ads Management"
        description="Upgrade to unlock Facebook & Instagram campaign management, AI-generated ad copy and creative, and automatic A/B testing."
        highlights={[
          { icon: Megaphone, label: 'Real campaigns', desc: 'Create, pause, and resume via Meta’s Ads API' },
          { icon: Sparkles, label: 'AI creative', desc: 'AI-written ad copy and generated images' },
          { icon: TrendingUp, label: 'Auto A/B testing', desc: 'Underperforming variants pause automatically' },
        ]}
      />
    );
  }

  const toggle = async (id) => {
    await api.patch(`/ads/campaigns/${id}/toggle`);
    load();
  };

  const totalSpend = campaigns.reduce((s, c) => s + (c.totalSpend || 0), 0);
  const totalLeads = campaigns.reduce((s, c) => s + (c.totalLeads || 0), 0);
  const avgCPL = totalLeads > 0 ? totalSpend / totalLeads : 0;

  return (
    <div className={PAGE}>
      <div className="mb-6">
        <PageHeader
          title="Ad Campaigns"
          actions={
            <button onClick={() => setShowCreate(true)}
              className="h-9 px-4 bg-[#0176D3] hover:bg-[#014486] text-white rounded text-[13px] font-semibold whitespace-nowrap">
              + New Campaign
            </button>
          }
        />
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          ['Total Spend', formatCurrency(totalSpend / 100, AD_SPEND_CURRENCY), '💸'],
          ['Total Leads', totalLeads, '👤'],
          ['Avg CPL', totalLeads > 0 ? formatCurrency(avgCPL / 100, AD_SPEND_CURRENCY) : '—', '📊'],
        ].map(([l, v, e]) => (
          <div key={l} className="bg-white border border-[#E5E5E5] rounded-lg p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
            <p className="text-xs text-[#706E6B] mb-1">{e} {l}</p>
            <p className="text-2xl font-bold text-[#080707]">{v}</p>
          </div>
        ))}
      </div>

      {/* Platform filter */}
      <div className="flex gap-2 mb-4">
        {[['', 'All'], ...Object.entries(PLATFORM_META).map(([k, v]) => [k, `${v.emoji} ${v.label}`])].map(([k, l]) => (
          <button key={k} onClick={() => setPlatformFilter(k)}
            className={`h-8 px-3 rounded text-[13px] font-semibold ${platformFilter === k ? 'bg-[#0176D3] text-white' : 'bg-white border border-[#C9C9C9] text-[#0176D3] hover:bg-[#F3F3F3]'}`}>
            {l}
          </button>
        ))}
      </div>

      {/* Campaign list */}
      {loading && <p className="text-[#706E6B] text-sm text-center py-8">Loading…</p>}
      <div className="space-y-3">
        {campaigns.map(c => {
          const pm = PLATFORM_META[c.platform] || {};
          const variantCount = c.variants?.length || 0;
          const activeVariants = c.variants?.filter(v => v.status === 'active').length || 0;
          return (
            <div key={c._id} className="bg-white border border-[#E5E5E5] rounded-lg p-5 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-xl"
                    style={{ backgroundColor: pm.color + '20' }}>
                    {pm.emoji}
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#080707]">{c.name}</h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-[#706E6B] capitalize">{pm.label} · {c.objective}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[c.status]}`}>{c.status}</span>
                      {c.abTestEnabled && <span className="text-xs bg-[#EEF4FF] text-[#0176D3] px-2 py-0.5 rounded-full font-medium">A/B</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggle(c._id)}
                    className={`text-xs px-3 py-1.5 rounded-full font-medium border  whitespace-nowrap ${c.status === 'active' ? 'border-[#FFB75D] text-[#8C4B02] hover:bg-[#FFF4E5]' : 'border-[#9FD9B4] text-[#036B3A] hover:bg-[#E3F3E8]'}`}>
                    {c.status === 'active' ? 'Pause' : 'Activate'}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mt-4">
                {[
                  ['Budget/day', formatCurrency((c.budget || 0) / 100, AD_SPEND_CURRENCY, { compact: true })],
                  ['Variants', `${activeVariants}/${variantCount} active`],
                  ['Leads', c.totalLeads || 0],
                  ['Spend', formatCurrency((c.totalSpend || 0) / 100, AD_SPEND_CURRENCY)],
                ].map(([l, v]) => (
                  <div key={l} className="bg-[#F7F9FB] rounded-lg p-3">
                    <p className="text-xs text-[#706E6B] mb-0.5">{l}</p>
                    <p className="text-sm font-semibold text-[#2E2E2E]">{v}</p>
                  </div>
                ))}
              </div>

              {c.winnerVariantId && (
                <p className="text-xs text-[#04844B] font-medium mt-2">🏆 A/B winner selected</p>
              )}
            </div>
          );
        })}
        {!loading && campaigns.length === 0 && (
          <div className="text-center py-16 border-2 border-dashed border-[#E5E5E5] rounded-lg text-[#706E6B]">
            <p className="text-lg mb-1">No campaigns yet</p>
            <p className="text-sm">Create your first campaign to start advertising</p>
          </div>
        )}
      </div>

      {showCreate && <CampaignForm onCreated={load} onClose={() => setShowCreate(false)} />}
    </div>
  );
}
